import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { resumeApi } from '../../services/api'
import { showSuccess, showError } from './appSlice'

const initialState = {
  // Данные резюме
  experience: [],
  education: [],
  certificates: [],
  languages: [],
  achievements: [],
  
  // Состояния загрузки
  loading: {
    experience: false,
    education: false,
    certificates: false,
    languages: false,
    achievements: false,
    all: false,
    pdf: false,
  },
  
  // Ошибки
  error: null,
  
  // Флаги инициализации
  initialized: false,
  
  // PDF экспорт
  pdfUrl: null,
  pdfGenerating: false,
}

// Async thunks для загрузки всех данных резюме
export const fetchAllResumeData = createAsyncThunk(
  'resume/fetchAllResumeData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeApi.getAll()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки данных резюме')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunks для опыта работы
export const fetchExperience = createAsyncThunk(
  'resume/fetchExperience',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeApi.getExperience()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки опыта работы')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createExperience = createAsyncThunk(
  'resume/createExperience',
  async (experienceData, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.createExperience(experienceData)
      
      if (response.success) {
        dispatch(showSuccess('Опыт работы добавлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания записи опыта')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при добавлении опыта работы'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateExperience = createAsyncThunk(
  'resume/updateExperience',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.updateExperience(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Опыт работы обновлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления опыта')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении опыта работы'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteExperience = createAsyncThunk(
  'resume/deleteExperience',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.deleteExperience(id)
      
      if (response.success) {
        dispatch(showSuccess('Опыт работы удален успешно'))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления опыта')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении опыта работы'))
      return rejectWithValue(error.message)
    }
  }
)

// Async thunks для образования
export const fetchEducation = createAsyncThunk(
  'resume/fetchEducation',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeApi.getEducation()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки образования')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createEducation = createAsyncThunk(
  'resume/createEducation',
  async (educationData, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.createEducation(educationData)
      
      if (response.success) {
        dispatch(showSuccess('Образование добавлено успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания записи образования')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при добавлении образования'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateEducation = createAsyncThunk(
  'resume/updateEducation',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.updateEducation(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Образование обновлено успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления образования')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении образования'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteEducation = createAsyncThunk(
  'resume/deleteEducation',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.deleteEducation(id)
      
      if (response.success) {
        dispatch(showSuccess('Образование удалено успешно'))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления образования')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении образования'))
      return rejectWithValue(error.message)
    }
  }
)
// Async thunks для сертификатов
export const fetchCertificates = createAsyncThunk(
  'resume/fetchCertificates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeApi.getCertificates()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки сертификатов')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createCertificate = createAsyncThunk(
  'resume/createCertificate',
  async (certificateData, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.createCertificate(certificateData)
      
      if (response.success) {
        dispatch(showSuccess('Сертификат добавлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания сертификата')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при добавлении сертификата'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateCertificate = createAsyncThunk(
  'resume/updateCertificate',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.updateCertificate(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Сертификат обновлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления сертификата')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении сертификата'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteCertificate = createAsyncThunk(
  'resume/deleteCertificate',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.deleteCertificate(id)
      
      if (response.success) {
        dispatch(showSuccess('Сертификат удален успешно'))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления сертификата')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении сертификата'))
      return rejectWithValue(error.message)
    }
  }
)

// Async thunks для языков
export const fetchLanguages = createAsyncThunk(
  'resume/fetchLanguages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeApi.getLanguages()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки языков')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createLanguage = createAsyncThunk(
  'resume/createLanguage',
  async (languageData, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.createLanguage(languageData)
      
      if (response.success) {
        dispatch(showSuccess('Язык добавлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания языка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при добавлении языка'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateLanguage = createAsyncThunk(
  'resume/updateLanguage',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.updateLanguage(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Язык обновлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления языка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении языка'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteLanguage = createAsyncThunk(
  'resume/deleteLanguage',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.deleteLanguage(id)
      
      if (response.success) {
        dispatch(showSuccess('Язык удален успешно'))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления языка')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении языка'))
      return rejectWithValue(error.message)
    }
  }
)

// Async thunks для достижений
export const fetchAchievements = createAsyncThunk(
  'resume/fetchAchievements',
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeApi.getAchievements()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки достижений')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createAchievement = createAsyncThunk(
  'resume/createAchievement',
  async (achievementData, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.createAchievement(achievementData)
      
      if (response.success) {
        dispatch(showSuccess('Достижение добавлено успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания достижения')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при добавлении достижения'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateAchievement = createAsyncThunk(
  'resume/updateAchievement',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.updateAchievement(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Достижение обновлено успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления достижения')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении достижения'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteAchievement = createAsyncThunk(
  'resume/deleteAchievement',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.deleteAchievement(id)
      
      if (response.success) {
        dispatch(showSuccess('Достижение удалено успешно'))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления достижения')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении достижения'))
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk для экспорта PDF
export const exportToPDF = createAsyncThunk(
  'resume/exportToPDF',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeApi.exportToPDF()
      
      if (response.success) {
        dispatch(showSuccess('PDF резюме сгенерировано успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка генерации PDF')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при генерации PDF'))
      return rejectWithValue(error.message)
    }
  }
)
const resumeSlice = createSlice({
  name: 'resume',
  initialState,
  reducers: {
    // Очистка ошибок
    clearError: (state) => {
      state.error = null
    },
    
    // Очистка URL PDF
    clearPdfUrl: (state) => {
      state.pdfUrl = null
    },
    
    // Сброс состояния инициализации
    resetInitialized: (state) => {
      state.initialized = false
    },
    
    // Обновление порядка элементов (для drag & drop в админке)
    updateExperienceOrder: (state, action) => {
      state.experience = action.payload
    },
    
    updateEducationOrder: (state, action) => {
      state.education = action.payload
    },
    
    updateCertificatesOrder: (state, action) => {
      state.certificates = action.payload
    },
    
    updateLanguagesOrder: (state, action) => {
      state.languages = action.payload
    },
    
    updateAchievementsOrder: (state, action) => {
      state.achievements = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch All Resume Data
    builder
      .addCase(fetchAllResumeData.pending, (state) => {
        state.loading.all = true
        state.error = null
      })
      .addCase(fetchAllResumeData.fulfilled, (state, action) => {
        state.loading.all = false
        state.experience = action.payload.experience || []
        state.education = action.payload.education || []
        state.certificates = action.payload.certificates || []
        state.languages = action.payload.languages || []
        state.achievements = action.payload.achievements || []
        state.initialized = true
        state.error = null
      })
      .addCase(fetchAllResumeData.rejected, (state, action) => {
        state.loading.all = false
        state.error = action.payload
      })
    
    // Experience
    builder
      .addCase(fetchExperience.pending, (state) => {
        state.loading.experience = true
        state.error = null
      })
      .addCase(fetchExperience.fulfilled, (state, action) => {
        state.loading.experience = false
        state.experience = action.payload
        state.error = null
      })
      .addCase(fetchExperience.rejected, (state, action) => {
        state.loading.experience = false
        state.error = action.payload
      })
    
    builder
      .addCase(createExperience.pending, (state) => {
        state.loading.experience = true
        state.error = null
      })
      .addCase(createExperience.fulfilled, (state, action) => {
        state.loading.experience = false
        state.experience.push(action.payload)
        state.error = null
      })
      .addCase(createExperience.rejected, (state, action) => {
        state.loading.experience = false
        state.error = action.payload
      })
    
    builder
      .addCase(updateExperience.pending, (state) => {
        state.loading.experience = true
        state.error = null
      })
      .addCase(updateExperience.fulfilled, (state, action) => {
        state.loading.experience = false
        const index = state.experience.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.experience[index] = action.payload
        }
        state.error = null
      })
      .addCase(updateExperience.rejected, (state, action) => {
        state.loading.experience = false
        state.error = action.payload
      })
    
    builder
      .addCase(deleteExperience.pending, (state) => {
        state.loading.experience = true
        state.error = null
      })
      .addCase(deleteExperience.fulfilled, (state, action) => {
        state.loading.experience = false
        state.experience = state.experience.filter(item => item.id !== action.payload)
        state.error = null
      })
      .addCase(deleteExperience.rejected, (state, action) => {
        state.loading.experience = false
        state.error = action.payload
      })
    
    // Education
    builder
      .addCase(fetchEducation.pending, (state) => {
        state.loading.education = true
        state.error = null
      })
      .addCase(fetchEducation.fulfilled, (state, action) => {
        state.loading.education = false
        state.education = action.payload
        state.error = null
      })
      .addCase(fetchEducation.rejected, (state, action) => {
        state.loading.education = false
        state.error = action.payload
      })
    
    builder
      .addCase(createEducation.pending, (state) => {
        state.loading.education = true
        state.error = null
      })
      .addCase(createEducation.fulfilled, (state, action) => {
        state.loading.education = false
        state.education.push(action.payload)
        state.error = null
      })
      .addCase(createEducation.rejected, (state, action) => {
        state.loading.education = false
        state.error = action.payload
      })
    
    builder
      .addCase(updateEducation.pending, (state) => {
        state.loading.education = true
        state.error = null
      })
      .addCase(updateEducation.fulfilled, (state, action) => {
        state.loading.education = false
        const index = state.education.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.education[index] = action.payload
        }
        state.error = null
      })
      .addCase(updateEducation.rejected, (state, action) => {
        state.loading.education = false
        state.error = action.payload
      })
    
    builder
      .addCase(deleteEducation.pending, (state) => {
        state.loading.education = true
        state.error = null
      })
      .addCase(deleteEducation.fulfilled, (state, action) => {
        state.loading.education = false
        state.education = state.education.filter(item => item.id !== action.payload)
        state.error = null
      })
      .addCase(deleteEducation.rejected, (state, action) => {
        state.loading.education = false
        state.error = action.payload
      })    // Certificates
    builder
      .addCase(fetchCertificates.pending, (state) => {
        state.loading.certificates = true
        state.error = null
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.loading.certificates = false
        state.certificates = action.payload
        state.error = null
      })
      .addCase(fetchCertificates.rejected, (state, action) => {
        state.loading.certificates = false
        state.error = action.payload
      })
    
    builder
      .addCase(createCertificate.pending, (state) => {
        state.loading.certificates = true
        state.error = null
      })
      .addCase(createCertificate.fulfilled, (state, action) => {
        state.loading.certificates = false
        state.certificates.push(action.payload)
        state.error = null
      })
      .addCase(createCertificate.rejected, (state, action) => {
        state.loading.certificates = false
        state.error = action.payload
      })
    
    builder
      .addCase(updateCertificate.pending, (state) => {
        state.loading.certificates = true
        state.error = null
      })
      .addCase(updateCertificate.fulfilled, (state, action) => {
        state.loading.certificates = false
        const index = state.certificates.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.certificates[index] = action.payload
        }
        state.error = null
      })
      .addCase(updateCertificate.rejected, (state, action) => {
        state.loading.certificates = false
        state.error = action.payload
      })
    
    builder
      .addCase(deleteCertificate.pending, (state) => {
        state.loading.certificates = true
        state.error = null
      })
      .addCase(deleteCertificate.fulfilled, (state, action) => {
        state.loading.certificates = false
        state.certificates = state.certificates.filter(item => item.id !== action.payload)
        state.error = null
      })
      .addCase(deleteCertificate.rejected, (state, action) => {
        state.loading.certificates = false
        state.error = action.payload
      })
    
    // Languages
    builder
      .addCase(fetchLanguages.pending, (state) => {
        state.loading.languages = true
        state.error = null
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.loading.languages = false
        state.languages = action.payload
        state.error = null
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.loading.languages = false
        state.error = action.payload
      })
    
    builder
      .addCase(createLanguage.pending, (state) => {
        state.loading.languages = true
        state.error = null
      })
      .addCase(createLanguage.fulfilled, (state, action) => {
        state.loading.languages = false
        state.languages.push(action.payload)
        state.error = null
      })
      .addCase(createLanguage.rejected, (state, action) => {
        state.loading.languages = false
        state.error = action.payload
      })
    
    builder
      .addCase(updateLanguage.pending, (state) => {
        state.loading.languages = true
        state.error = null
      })
      .addCase(updateLanguage.fulfilled, (state, action) => {
        state.loading.languages = false
        const index = state.languages.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.languages[index] = action.payload
        }
        state.error = null
      })
      .addCase(updateLanguage.rejected, (state, action) => {
        state.loading.languages = false
        state.error = action.payload
      })
    
    builder
      .addCase(deleteLanguage.pending, (state) => {
        state.loading.languages = true
        state.error = null
      })
      .addCase(deleteLanguage.fulfilled, (state, action) => {
        state.loading.languages = false
        state.languages = state.languages.filter(item => item.id !== action.payload)
        state.error = null
      })
      .addCase(deleteLanguage.rejected, (state, action) => {
        state.loading.languages = false
        state.error = action.payload
      })
    
    // Achievements
    builder
      .addCase(fetchAchievements.pending, (state) => {
        state.loading.achievements = true
        state.error = null
      })
      .addCase(fetchAchievements.fulfilled, (state, action) => {
        state.loading.achievements = false
        state.achievements = action.payload
        state.error = null
      })
      .addCase(fetchAchievements.rejected, (state, action) => {
        state.loading.achievements = false
        state.error = action.payload
      })
    
    builder
      .addCase(createAchievement.pending, (state) => {
        state.loading.achievements = true
        state.error = null
      })
      .addCase(createAchievement.fulfilled, (state, action) => {
        state.loading.achievements = false
        state.achievements.push(action.payload)
        state.error = null
      })
      .addCase(createAchievement.rejected, (state, action) => {
        state.loading.achievements = false
        state.error = action.payload
      })
    
    builder
      .addCase(updateAchievement.pending, (state) => {
        state.loading.achievements = true
        state.error = null
      })
      .addCase(updateAchievement.fulfilled, (state, action) => {
        state.loading.achievements = false
        const index = state.achievements.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.achievements[index] = action.payload
        }
        state.error = null
      })
      .addCase(updateAchievement.rejected, (state, action) => {
        state.loading.achievements = false
        state.error = action.payload
      })
    
    builder
      .addCase(deleteAchievement.pending, (state) => {
        state.loading.achievements = true
        state.error = null
      })
      .addCase(deleteAchievement.fulfilled, (state, action) => {
        state.loading.achievements = false
        state.achievements = state.achievements.filter(item => item.id !== action.payload)
        state.error = null
      })
      .addCase(deleteAchievement.rejected, (state, action) => {
        state.loading.achievements = false
        state.error = action.payload
      })
    
    // PDF Export
    builder
      .addCase(exportToPDF.pending, (state) => {
        state.loading.pdf = true
        state.pdfGenerating = true
        state.error = null
      })
      .addCase(exportToPDF.fulfilled, (state, action) => {
        state.loading.pdf = false
        state.pdfGenerating = false
        state.pdfUrl = action.payload.url || action.payload
        state.error = null
      })
      .addCase(exportToPDF.rejected, (state, action) => {
        state.loading.pdf = false
        state.pdfGenerating = false
        state.error = action.payload
      })
  },
})
export const {
  clearError,
  clearPdfUrl,
  resetInitialized,
  updateExperienceOrder,
  updateEducationOrder,
  updateCertificatesOrder,
  updateLanguagesOrder,
  updateAchievementsOrder,
} = resumeSlice.actions

// Selectors
export const selectResumeData = (state) => ({
  experience: state.resume.experience,
  education: state.resume.education,
  certificates: state.resume.certificates,
  languages: state.resume.languages,
  achievements: state.resume.achievements,
})

export const selectResumeLoading = (state) => state.resume.loading

export const selectResumeError = (state) => state.resume.error

export const selectResumeInitialized = (state) => state.resume.initialized

export const selectExperience = (state) => state.resume.experience

export const selectEducation = (state) => state.resume.education

export const selectCertificates = (state) => state.resume.certificates

export const selectLanguages = (state) => state.resume.languages

export const selectAchievements = (state) => state.resume.achievements

export const selectPdfUrl = (state) => state.resume.pdfUrl

export const selectPdfGenerating = (state) => state.resume.pdfGenerating

// Селекторы для статистики
export const selectResumeStats = (state) => {
  const { experience, education, certificates, languages, achievements } = state.resume
  
  return {
    totalExperience: experience.length,
    totalEducation: education.length,
    totalCertificates: certificates.length,
    totalLanguages: languages.length,
    totalAchievements: achievements.length,
    yearsOfExperience: experience.reduce((total, exp) => {
      if (exp.start_date && exp.end_date) {
        const start = new Date(exp.start_date)
        const end = new Date(exp.end_date)
        const years = (end - start) / (1000 * 60 * 60 * 24 * 365)
        return total + years
      }
      return total
    }, 0),
  }
}

// Селектор для сортированного опыта работы (по дате начала, новые сверху)
export const selectSortedExperience = (state) => {
  return [...state.resume.experience].sort((a, b) => {
    const dateA = new Date(a.start_date || 0)
    const dateB = new Date(b.start_date || 0)
    return dateB - dateA
  })
}

// Селектор для сортированного образования (по дате окончания, новые сверху)
export const selectSortedEducation = (state) => {
  return [...state.resume.education].sort((a, b) => {
    const dateA = new Date(a.end_date || a.start_date || 0)
    const dateB = new Date(b.end_date || b.start_date || 0)
    return dateB - dateA
  })
}

// Селектор для сертификатов, сгруппированных по категориям
export const selectCertificatesByCategory = (state) => {
  const certificates = state.resume.certificates
  const grouped = {}
  
  certificates.forEach(cert => {
    const category = cert.category || 'Другое'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push(cert)
  })
  
  return grouped
}

// Селектор для достижений, сгруппированных по категориям
export const selectAchievementsByCategory = (state) => {
  const achievements = state.resume.achievements
  const grouped = {}
  
  achievements.forEach(achievement => {
    const category = achievement.category || 'Другое'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push(achievement)
  })
  
  return grouped
}

export default resumeSlice.reducer